/*  UI  */
import { Box, Card, Flex } from '@auviknetworks/iceberg-ui';
import { Avatar, Divider, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Image } from 'application/Shared/Components/';
import { ApplicationBubbles } from 'application/Shared/Components/Avatars/Applications';
import { FontSizes } from 'application/Shared/Constants';
import { Route } from 'application/Shared/Helpers';
import type {
    LicenseType,
    TenantAppLicenseUsage
} from 'application/Shared/types/TenantAppLicenseUsage';
import { Application } from 'application/Shared/types/application';
import { User } from 'application/Shared/types/user';

/*  React  */
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import { type EntityPanel as EntityPanelType } from '../types';
import { Panel } from './Panel';
import { usePanelContext } from './PanelContext';

interface Category {
    applications: Application[];
    count: number;
    id: string;
    name: string;
    subcategory: string;
}

const EntityPanelContent = () => {
    const {
        queryResults: { data },
        schema: { format },
        panelState: { isFullScreen },
        setNoData
    } = usePanelContext<EntityPanelType>();

    const theme = useTheme();
    const { t } = useTranslation();
    const { clientId, partnerId } = useParams();

    const baseAdminRoute = Route.buildBaseAdminRoute({
        clientId: clientId!,
        partnerId
    });

    const usersWithApps =
        format?.variant === 'user' &&
        data?.filter(
            (user: User) => user.applications && user.applications.length > 0
        );

    const underutilizedLicenses =
        format?.variant === 'license' &&
        data?.filter(
            (app: TenantAppLicenseUsage) =>
                app.licenses?.underutilized?.total !== 0
        );

    const overlappingAppsCategories =
        format?.variant === 'application' &&
        data?.filter((category: Category) => category.applications?.length > 1);

    useEffect(() => {
        const hasData = data && data.length > 0;
        const hasRelevantData =
            (format?.variant === 'user' && usersWithApps?.length > 0) ||
            (format?.variant === 'license' &&
                underutilizedLicenses?.length > 0) ||
            (format?.variant === 'application' &&
                overlappingAppsCategories?.length > 0);

        setNoData(!hasData || !hasRelevantData);
    }, [
        data,
        format?.variant,
        usersWithApps,
        underutilizedLicenses,
        overlappingAppsCategories,
        setNoData
    ]);

    if (format?.variant === 'user' && usersWithApps) {
        // Sort users by application count (descending) and limit to top 6
        const sortedUsers = [...usersWithApps]
            .sort(
                (a: User, b: User) =>
                    (b.applications?.length || 0) -
                    (a.applications?.length || 0)
            )
            .slice(0, isFullScreen ? 12 : 6);

        return (
            <Box
                display="grid"
                gridTemplateColumns="repeat(2, 1fr)"
                sx={{ gap: '16px', marginBottom: '24px', overflow: 'auto' }}
            >
                {sortedUsers.map((user: User) => (
                    <Link
                        data-trackid="entityPanelLink_user"
                        key={user.id}
                        to={`${baseAdminRoute}/user/${user.id}/overview`}
                        style={{ textDecoration: 'none' }}
                    >
                        <Card
                            sx={{
                                alignItems: 'flex-start',
                                borderColor:
                                    theme.palette.border.highlightedCard,
                                display: 'flex',
                                flexDirection: 'column',
                                height: '100%',
                                padding: '8px',
                                width: '100%'
                            }}
                        >
                            <Typography
                                sx={{
                                    'a:hover &': {
                                        color: 'hover.tertiary'
                                    },
                                    fontSize: FontSizes[1],
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    width: '100%'
                                }}
                            >
                                {user.name}
                            </Typography>
                            <Box
                                sx={{
                                    alignItems: isFullScreen
                                        ? 'center'
                                        : 'flex-start',
                                    display: isFullScreen ? 'flex' : 'block',
                                    justifyContent: 'space-between',
                                    width: '100%'
                                }}
                            >
                                <Typography
                                    sx={{
                                        'a:hover &': {
                                            color: 'hover.tertiary'
                                        },
                                        fontSize: FontSizes[0],
                                        marginBottom: '8px',
                                        maxWidth: '150px',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        width: '100%'
                                    }}
                                >
                                    {user.email}
                                </Typography>
                                <ApplicationBubbles
                                    applications={user.applications || []}
                                    max={4}
                                />
                            </Box>
                        </Card>
                    </Link>
                ))}
            </Box>
        );
    }

    if (format?.variant === 'application' && overlappingAppsCategories) {
        const overlappingAppsToDisplay = [...overlappingAppsCategories].slice(
            0,
            isFullScreen ? 12 : 6
        );

        return (
            <Box
                display="grid"
                gridTemplateColumns="repeat(2, 1fr)"
                sx={{ gap: '16px', marginBottom: '24px' }}
            >
                {overlappingAppsToDisplay?.map((category: Category) => (
                    <Card
                        key={category.id || category.name}
                        sx={{
                            alignItems: 'flex-start',
                            display: 'flex',
                            flexDirection: 'column',
                            padding: '8px',
                            width: '100%'
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: FontSizes[0],
                                marginBottom: '16px'
                            }}
                        >
                            {category.name}
                        </Typography>
                        <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            width="100%"
                        >
                            <Typography
                                variant="h4"
                                color={theme.palette.text.overlappingAppsCount}
                            >
                                {category.count}
                            </Typography>
                            <Box>
                                <ApplicationBubbles
                                    applications={category.applications || []}
                                    max={3}
                                />
                            </Box>
                        </Box>
                    </Card>
                ))}
            </Box>
        );
    }
    if (format?.variant === 'license' && underutilizedLicenses) {
        const underutilizedLicensesToDisplay = [...underutilizedLicenses].slice(
            0,
            isFullScreen ? 12 : 4
        );
        return (
            <Box
                sx={{
                    display: 'grid',
                    gap: '16px',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    marginBottom: '24px'
                }}
            >
                {underutilizedLicensesToDisplay?.map(
                    (app: TenantAppLicenseUsage) => {
                        const licenseTypes =
                            app.licenses?.underutilized?.types ?? [];
                        return (
                            <Link
                                data-trackid="entityPanelLink_license"
                                key={app.id}
                                to={`${baseAdminRoute}/application/${app.id}/licenses`}
                            >
                                <Card
                                    sx={{
                                        alignItems: 'flex-start',
                                        borderColor:
                                            theme.palette.border
                                                .highlightedCard,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        height: '100%',
                                        padding: '8px',
                                        width: '100%'
                                    }}
                                >
                                    <Flex
                                        sx={{
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            marginBottom: '16px',
                                            width: '100%'
                                        }}
                                    >
                                        <Flex
                                            sx={{
                                                alignItems: 'center',
                                                gap: '8px'
                                            }}
                                        >
                                            <Avatar
                                                sx={{
                                                    backgroundColor:
                                                        'avatar.appsAvatar.backgroundColor',
                                                    border: '1px solid',
                                                    borderColor:
                                                        'border.appBubbles',
                                                    height: 24,
                                                    width: 24
                                                }}
                                            >
                                                <Image
                                                    imageId={app.favico}
                                                    size="24"
                                                />
                                            </Avatar>
                                            <Typography
                                                sx={{
                                                    'a:hover &': {
                                                        color: 'hover.tertiary'
                                                    },
                                                    fontWeight: '600'
                                                }}
                                            >
                                                {app.name}
                                            </Typography>
                                        </Flex>
                                        <Typography
                                            sx={{
                                                'a:hover &': {
                                                    color: 'hover.tertiary'
                                                },
                                                fontWeight: '600'
                                            }}
                                        >
                                            {app.licenses?.underutilized?.total}
                                        </Typography>
                                    </Flex>
                                    {licenseTypes
                                        // Sort by count descending, then by type ascending
                                        .sort(
                                            (a, b) =>
                                                b.count - a.count ||
                                                a.type.localeCompare(b.type)
                                        )
                                        // Only show the first 3 licenses
                                        .slice(0, 3)
                                        .map(
                                            (
                                                license: LicenseType['types'][number]
                                            ) => (
                                                <Flex
                                                    key={license.type}
                                                    sx={{
                                                        justifyContent:
                                                            'space-between',
                                                        width: '100%'
                                                    }}
                                                >
                                                    <Typography
                                                        sx={{
                                                            'a:hover &': {
                                                                color: 'hover.tertiary'
                                                            },
                                                            fontSize:
                                                                FontSizes[0]
                                                        }}
                                                    >
                                                        {license.type}
                                                    </Typography>
                                                    <Typography
                                                        sx={{
                                                            'a:hover &': {
                                                                color: 'hover.tertiary'
                                                            },
                                                            fontSize:
                                                                FontSizes[0]
                                                        }}
                                                    >
                                                        {license.count}
                                                    </Typography>
                                                </Flex>
                                            )
                                        )}

                                    {licenseTypes.length > 3 && (
                                        <>
                                            <Divider
                                                sx={{
                                                    backgroundColor:
                                                        theme.palette.border
                                                            .darkerDivider,
                                                    marginY: '8px',
                                                    opacity: 1,
                                                    width: '100%'
                                                }}
                                            />
                                            <Typography
                                                sx={{
                                                    'a:hover &': {
                                                        color: 'hover.tertiary'
                                                    },
                                                    fontSize: FontSizes[0]
                                                }}
                                            >
                                                {t(
                                                    'Common.additionalLicenses',
                                                    {
                                                        count:
                                                            licenseTypes.length -
                                                            3
                                                    }
                                                )}
                                            </Typography>
                                        </>
                                    )}
                                </Card>
                            </Link>
                        );
                    }
                )}
            </Box>
        );
    }

    return null;
};

export const EntityPanel = ({ panel }: { panel: EntityPanelType }) => (
    <Panel<EntityPanelType> panel={panel}>
        <Box padding="0 24px" height="100%">
            <EntityPanelContent />
        </Box>
    </Panel>
);
